/**
 * Prevent open redirects: only same-origin relative paths (via URL parser)
 * or exact allowlisted absolute / custom-scheme URIs (no prefix matching).
 */

/** Exact OAuth / deep-link redirect URIs (must match API allowlist seeds). */
export const OAUTH_REDIRECT_URI_ALLOWLIST = [
  "vetty://auth/callback",
  "vetty-studio://auth/callback",
] as const;

const APP_PATH_BASE = "https://vetty.local";

const decodeFully = (value: string): string | null => {
  let current = value;
  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(current);
      if (next === current) return current;
      current = next;
    } catch {
      return null;
    }
  }
  return current;
};

const hasDangerousChars = (value: string): boolean =>
  value.includes("\\") ||
  value.includes("\0") ||
  /[\u0000-\u001f\u007f]/.test(value);

/**
 * Same-app relative path only (locale-aware callers add locale separately).
 * Rejects protocol-relative, backslash, scheme, and encoded open-redirect tricks.
 */
export const isSafeAppPath = (path: string | null | undefined): boolean => {
  if (!path || typeof path !== "string") return false;
  const trimmed = path.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return false;
  if (hasDangerousChars(trimmed)) return false;

  const decoded = decodeFully(trimmed);
  if (!decoded) return false;
  if (!decoded.startsWith("/") || decoded.startsWith("//")) return false;
  if (hasDangerousChars(decoded)) return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(decoded)) return false;

  try {
    const url = new URL(trimmed, APP_PATH_BASE);
    if (url.origin !== APP_PATH_BASE) return false;
    if (url.username || url.password) return false;
    if (url.pathname.includes("\\") || url.pathname.startsWith("//")) return false;
    // Path-only: ignore search/hash for destination safety of the path itself
    return url.pathname.startsWith("/") && !url.pathname.startsWith("//");
  } catch {
    return false;
  }
};

const isExactAllowlistedHttpLocalhost = (target: string): boolean => {
  try {
    const url = new URL(target);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    if (url.hostname !== "localhost" && url.hostname !== "127.0.0.1") {
      return false;
    }
    if (url.username || url.password) return false;
    // Exact entries for local OAuth test callbacks (path must be /auth/callback)
    return url.pathname === "/auth/callback";
  } catch {
    return false;
  }
};

/** Exact allowlist match for OAuth redirect_uri (no prefix). */
export const isExactOAuthRedirectUri = (
  target: string | null | undefined,
): boolean => {
  if (!target || typeof target !== "string") return false;
  const trimmed = target.trim();
  if (!trimmed || hasDangerousChars(trimmed)) return false;

  if ((OAUTH_REDIRECT_URI_ALLOWLIST as readonly string[]).includes(trimmed)) {
    return true;
  }
  return isExactAllowlistedHttpLocalhost(trimmed);
};

export const isSafeRedirectTarget = (
  target: string | null | undefined,
): boolean => {
  if (!target) return false;
  if (isSafeAppPath(target)) return true;
  return isExactOAuthRedirectUri(target);
};

export const withLocale = (locale: string, path: string): string => {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized === `/${locale}` || normalized.startsWith(`/${locale}/`)) {
    return normalized;
  }
  return `/${locale}${normalized}`;
};

/** Resolve ?next= query to a safe in-app path; fallback when missing/unsafe. */
export const resolveSafeNextPath = (
  next: string | null | undefined,
  fallback: string,
  _locale?: string,
): string => {
  const candidate = (next ?? "").trim();
  if (isSafeAppPath(candidate)) {
    // Preserve full relative URL including query (authorize continuation).
    try {
      const url = new URL(candidate, APP_PATH_BASE);
      return `${url.pathname}${url.search}`;
    } catch {
      return fallback;
    }
  }
  return fallback;
};

/**
 * Local OIDC authorize continuation only (`/oidc/authorize` + query).
 * Not an OAuth client redirect_uri — never confuse the two.
 */
export const isSafeAuthorizeContinuationPath = (
  path: string | null | undefined,
): boolean => {
  if (!isSafeAppPath(path)) return false;
  try {
    const url = new URL(String(path).trim(), APP_PATH_BASE);
    return url.pathname === "/oidc/authorize";
  } catch {
    return false;
  }
};

/** Build safe continuation from this request's authorize path + search. */
export const buildSafeAuthorizeContinuation = (
  pathname: string,
  search: string = "",
): string | null => {
  const query = search.startsWith("?") || search === "" ? search : `?${search}`;
  const candidate = `${pathname}${query}`;
  if (!isSafeAuthorizeContinuationPath(candidate)) return null;
  try {
    const url = new URL(candidate, APP_PATH_BASE);
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
};

/** Read only the OIDC authorize continuation carried by Auth's `next` query. */
export const getSafeAuthorizeContinuation = (
  search: string | null | undefined,
): string | null => {
  if (!search) return null;
  const next = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("next");
  if (!isSafeAuthorizeContinuationPath(next)) return null;
  try {
    const url = new URL(String(next).trim(), APP_PATH_BASE);
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
};

/** Preserve only the approved OIDC authorize continuation from a decoded next value. */
export const buildVerificationHrefForNext = (
  locale: string,
  next: string | null | undefined,
): string => {
  const path = withLocale(locale, "/verification");
  if (!isSafeAuthorizeContinuationPath(next)) return path;
  try {
    const url = new URL(String(next).trim(), APP_PATH_BASE);
    return `${path}?next=${encodeURIComponent(`${url.pathname}${url.search}`)}`;
  } catch {
    return path;
  }
};

/** Preserve a safe OIDC continuation while routing an authenticated unverified user. */
export const buildVerificationHrefWithNext = (
  locale: string,
  search: string | null | undefined,
): string => {
  const path = withLocale(locale, "/verification");
  const continuation = getSafeAuthorizeContinuation(search);
  return continuation ? `${path}?next=${encodeURIComponent(continuation)}` : path;
};

/** Locale login URL that preserves a safe `next` continuation (relative). */
export const buildLoginHrefWithNext = (
  locale: string,
  next: string | null | undefined,
): string => {
  const loginPath = withLocale(locale, "/login");
  const safe = (next ?? "").trim();
  if (!isSafeAppPath(safe)) return loginPath;
  try {
    const url = new URL(safe, APP_PATH_BASE);
    const preserved = `${url.pathname}${url.search}`;
    return `${loginPath}?next=${encodeURIComponent(preserved)}`;
  } catch {
    return loginPath;
  }
};

/** Locale sign-up URL that preserves a safe `next` continuation. */
export const buildSignUpHrefWithNext = (
  locale: string,
  next: string | null | undefined,
): string => {
  const signUpPath = withLocale(locale, "/sign-up");
  const safe = (next ?? "").trim();
  if (!isSafeAppPath(safe)) return signUpPath;
  try {
    const url = new URL(safe, APP_PATH_BASE);
    const preserved = `${url.pathname}${url.search}`;
    return `${signUpPath}?next=${encodeURIComponent(preserved)}`;
  } catch {
    return signUpPath;
  }
};
